import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { API_URL, copyToClipboard } from './adminUtils';
import {
  CheckCircle2,
  Clock,
  Users,
  Download,
  RefreshCw,
  Trash2,
} from 'lucide-react';

const SignaturesManagement = () => {
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/signatures/status`);
      setSignatureStatus(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);
  
  const handleDeleteSignature = async (associationId) => {
    if (!window.confirm('Are you sure you want to delete this signature? The association will need to sign again.')) return;
    
    try {
      await axios.delete(`${API_URL}/signatures/${associationId}`);
      toast.success('Signature deleted. Association can now re-sign.');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to delete signature');
    }
  };
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/constitution/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CCA_Constitution_Signed.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF with signatures downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold">Signature Tracking</h1>
          <p className="text-muted-foreground">Monitor and manage constitution signatures</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleDownloadPDF} className="bg-cameroon-green hover:bg-cameroon-green/90" data-testid="download-signed-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Signed PDF
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="bg-cameroon-green/5 border-cameroon-green/20">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-cameroon-green mx-auto mb-2" />
            <div className="text-4xl font-bold text-cameroon-green">{signatureStatus?.signed || 0}</div>
            <p className="text-sm text-muted-foreground mt-1">Signed</p>
          </CardContent>
        </Card>
        <Card className="bg-cameroon-yellow/5 border-cameroon-yellow/20">
          <CardContent className="p-6 text-center">
            <Clock className="w-8 h-8 text-cameroon-yellow mx-auto mb-2" />
            <div className="text-4xl font-bold text-cameroon-yellow">{signatureStatus?.pending || 0}</div>
            <p className="text-sm text-muted-foreground mt-1">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <div className="text-4xl font-bold">{signatureStatus?.total || 12}</div>
            <p className="text-sm text-muted-foreground mt-1">Total Associations</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Signatures List */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Signed */}
        <Card>
          <CardHeader>
            <CardTitle className="text-cameroon-green flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Signed Associations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {signatureStatus?.signed_associations?.map((assoc) => (
              <div 
                key={assoc.association_id}
                className="p-4 rounded-lg bg-cameroon-green/5 border border-cameroon-green/20 flex items-center justify-between"
              >
                <div>
                  <h3 className="font-medium">{assoc.association_name}</h3>
                  <p className="text-sm text-muted-foreground">President: {assoc.president_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Signed: {new Date(assoc.signed_at).toLocaleString()}
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleDeleteSignature(assoc.association_id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {signatureStatus?.signed_associations?.length === 0 && (
              <p className="text-muted-foreground text-center py-4">No signatures yet</p>
            )}
          </CardContent>
        </Card>
        
        {/* Pending */}
        <Card>
          <CardHeader>
            <CardTitle className="text-cameroon-yellow flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending Signatures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {signatureStatus?.pending_associations?.map((assoc) => (
              <div 
                key={assoc.association_id}
                className="p-4 rounded-lg bg-muted/50 border flex items-center justify-between"
              >
                <div>
                  <h3 className="font-medium">{assoc.association_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {assoc.president_name ? `President: ${assoc.president_name}` : 'No president registered'}
                  </p>
                </div>
                <Badge variant="outline">Awaiting</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      
      {/* Public Signing Link */}
      <Card>
        <CardHeader>
          <CardTitle>Public Signing Link</CardTitle>
          <CardDescription>Share this link with association presidents to sign the constitution</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input 
              readOnly 
              value={`${window.location.origin}/sign`}
              className="font-mono text-sm"
            />
            <Button 
              variant="outline"
              onClick={async () => {
                const success = await copyToClipboard(`${window.location.origin}/sign`);
                if (success) {
                  toast.success('Link copied to clipboard');
                } else {
                  toast.error('Could not copy. Please select and copy manually.');
                }
              }}
            >
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignaturesManagement;
